import { computed, ref, type Ref } from 'vue'
import type { InspectorPanel } from '../model/types'
import type { FlowDocument } from './useFlowDocument'

/** Node/edge selection. Nodes and an edge are never selected at the same time. */
export function useFlowSelection(document: FlowDocument, inspectorPanel: Ref<InspectorPanel>) {
  const selectedNodeId = ref<string | null>(null)
  const selectedNodeIds = ref<string[]>([])
  const selectedEdgeId = ref<string | null>(null)

  const selectedNode = computed(
    () => document.nodes.value.find((node) => node.id === selectedNodeId.value) || null,
  )
  const hasSelection = computed(
    () => selectedNodeIds.value.length > 0 || Boolean(selectedEdgeId.value),
  )

  /** Selects existing nodes; the first becomes the inspected node. */
  const selectNodes = (ids: string[]) => {
    const unique = [...new Set(ids.filter((id) => document.nodeById.value.has(id)))]
    selectedNodeIds.value = unique
    selectedNodeId.value = unique[0] || null
    selectedEdgeId.value = null
  }

  /** Selects one node and shows its settings. */
  const selectNode = (nodeId: string | null) => {
    if (!nodeId) {
      selectNodes([])
      return
    }
    selectNodes([nodeId])
    if (inspectorPanel.value !== 'flow') inspectorPanel.value = 'flow'
  }

  const selectEdge = (edgeId: string) => {
    selectedEdgeId.value = edgeId
    selectedNodeId.value = null
    selectedNodeIds.value = []
  }

  const clearSelection = () => {
    selectedNodeId.value = null
    selectedNodeIds.value = []
    selectedEdgeId.value = null
  }

  return {
    selectedNodeId,
    selectedNodeIds,
    selectedEdgeId,
    selectedNode,
    hasSelection,
    selectNodes,
    selectNode,
    selectEdge,
    clearSelection,
  }
}

export type FlowSelection = ReturnType<typeof useFlowSelection>
